import assign from 'lodash/assign';
import remove from 'lodash/remove';
import moment from '@/utils/moment-wrapper.ts';
import {
    fetchConversation,
    fetchUserConversations,
    getAskPendoPostRequest,
    getInitialState,
    cleanupIncompleteStoppedMessages,
    parseMessageContent,
} from './ask-pendo.utils.js';
import { StreamingProcessor } from './ask-pendo.streaming-processor.js';

const askPendo = {
    namespaced: true,
    state: getInitialState(),
    mutations: {
        addMessage (state, { conversationId, type, content, timestamp, metadata = {}, data = {}, streamId = null }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            const message = {
                type,
                content,
                timestamp,
                metadata,
                ...data,
            };

            // Add streamId if provided
            if (streamId) {
                message.streamId = streamId;
            }

            conversation.messages.push(message);

            // Clean up redundant stopped messages
            cleanupIncompleteStoppedMessages(conversation.messages);
        },
        addStateToConversation (state, { conversationId, fieldName, fieldData }) {
            const conversation = state.conversations[conversationId];
            conversation.state_details[fieldName] = fieldData;
        },
        clearActiveStreamId (state, { conversationId }) {
            if (state.activeStreamIds) {
                delete state.activeStreamIds[conversationId];
            }
        },
        clearAbortController (state, { conversationId }) {
            delete state.abortControllers[conversationId];
        },
        clearArtifactMenuItems (state, { artifactId }) {
            delete state.artifactMenuItems[artifactId];
        },
        removeLastExchange (state, { conversationId }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            const { messages, artifacts } = conversation;

            // find the last user message index
            let lastUserIndex = -1;
            for (let i = messages.length - 1; i >= 0; i--) {
                if (messages[i]?.type === 'user') {
                    lastUserIndex = i;
                    break;
                }
            }
            if (lastUserIndex === -1) return; // nothing to remove

            // collect artifact ids from messages being removed
            const removedArtifactIds = new Set();
            for (let i = lastUserIndex; i < messages.length; i++) {
                const ids = messages[i]?.metadata?.artifact_ids;
                if (Array.isArray(ids)) {
                    for (const id of ids) removedArtifactIds.add(id);
                }
            }

            // remove everything from last user message onward
            messages.length = lastUserIndex; // truncate in place

            // delete associated artifacts
            if (artifacts && removedArtifactIds.size) {
                for (const id of removedArtifactIds) {
                    delete artifacts[id];
                }
            }

            // clear last_artifact_id if it was removed
            if (conversation.last_artifact_id && removedArtifactIds.has(conversation.last_artifact_id)) {
                delete conversation.last_artifact_id;
            }
        },
        removeIncompleteMessages (state, { conversationId, streamId = null }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            const { messages } = conversation;
            const isBlank = (item) => !item || item.trim() === '';

            // Helper: Check if message is incomplete assistant
            const isIncompleteAssistant = (msg, targetStreamId) =>
                msg.type === 'assistant' &&
                msg.tool_calls?.length > 0 &&
                isBlank(parseMessageContent(msg).content) &&
                (!targetStreamId || msg.streamId === targetStreamId);

            // Helper: Clean assistant message
            const cleanAssistant = (msg) => {
                const cleaned = { ...msg };
                delete cleaned.tool_calls;
                if (isBlank(parseMessageContent(cleaned).content)) cleaned.content = 'Request was stopped by the user.';

                return cleaned;
            };

            const toolCallIds = new Set();

            // First pass: find incomplete assistant and collect tool call IDs
            for (let i = messages.length - 1; i >= 0; i--) {
                const msg = messages[i];
                if (isIncompleteAssistant(msg, streamId)) {
                    for (const tc of msg.tool_calls) toolCallIds.add(tc.id);
                    messages[i] = cleanAssistant(msg);
                    break; // Only process the first (last) incomplete assistant found
                }
            }

            // Second pass: remove tool messages if we found any tool call IDs
            if (toolCallIds.size > 0) {
                remove(messages, (msg) => msg && msg.type === 'tool' && toolCallIds.has(msg.tool_call_id));
            }
            // tidy up any redundant "stopped" right before a real assistant message
            cleanupIncompleteStoppedMessages(messages);
        },
        setAbortController (state, { conversationId, controller }) {
            state.abortControllers[conversationId] = controller;
        },
        setActiveStreamId (state, { conversationId, streamId }) {
            state.activeStreamIds[conversationId] = streamId;
        },
        setArtifactConfig (state, { artifactId, config }) {
            state.artifactConfig[artifactId] = assign({}, state.artifactConfig[artifactId], config);
        },
        setArtifactMenuItems (state, { artifactId, items }) {
            state.artifactMenuItems[artifactId] = items;
        },
        setConfigDrawerState (state, { isOpen }) {
            state.configDrawerOpen = isOpen;
        },
        setConversation (state, { id, conversation }) {
            state.conversations[id] = conversation;
        },
        setUserConversations (state, { conversations }) {
            state.userConversations = conversations;
        },
        setEmployeeConfig (state, { employeeConfig }) {
            state.employeeConfig = employeeConfig;
        },
        setCustomInstructions (state, { customInstructions }) {
            state.customInstructions = customInstructions;
        },
        setResponsePending (state, { id, isPending }) {
            state.pendingResponses[id] = isPending;
        },
        setWorkflowState (state, { conversationId, workflowState }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            conversation.workflow_state = workflowState;
        },
        setPlanState (state, { conversationId, planState }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            conversation.plan = planState;
        },
        updateTodo (state, { conversationId, updatedTodo }) {
            const conversation = state.conversations[conversationId];
            if (!conversation) return;

            const todos = conversation.plan.todos.map((t) => (t.id === updatedTodo.id ? updatedTodo : t));
            conversation.plan = { ...conversation.plan, todos };
        },
        updateArtifact (state, { conversationId, artifactId, updates }) {
            const conversation = state.conversations[conversationId];
            if (!conversation?.artifacts?.[artifactId]) return;

            const artifact = conversation.artifacts[artifactId];

            artifact.data = {
                ...artifact.data,
                ...updates,
            };
        },
    },
    actions: {
        addMessage ({ commit }, { conversationId, type, content, metadata = {}, data = {}, streamId = null }) {
            commit('addMessage', {
                conversationId,
                type,
                content,
                timestamp: moment().toISOString(),
                metadata,
                data,
                streamId,
            });
        },
        async createConversation ({ commit }, {
            id,
            prompt,
            type = 'user',
            metadata = {},
            artifacts = {},
            // eslint-disable-next-line camelcase
            user_context = {},
            initialConversation = null,
        }) {
            if (initialConversation && initialConversation.conversationId && initialConversation.messages && initialConversation.use_provided_artifact && initialConversation.artifact) {
                const conversation = { ...initialConversation };
                commit('setConversation', { id: conversation.conversationId, conversation });

                return conversation;
            }

            const conversation = {
                conversationId: id,
                messages: [],
                artifacts,
                // eslint-disable-next-line camelcase
                user_context,
            };

            commit('setConversation', { id, conversation });

            const hasPrompt = typeof prompt === 'string' && prompt.trim().length > 0;
            if (hasPrompt) {
                commit('addMessage', {
                    conversationId: id,
                    type,
                    content: prompt,
                    timestamp: moment().toISOString(),
                    metadata,
                });
            }

            return conversation;
        },
        clearArtifactMenuItems ({ commit }, artifactId) {
            commit('clearArtifactMenuItems', { artifactId });
        },
        async loadAll ({ state, commit }, { reFetch = false } = {}) {
            if (state.userConversations.length > 0 && !reFetch) {
                return state.userConversations;
            }

            const { data } = await fetchUserConversations();
            commit('setUserConversations', { conversations: data });

            return data;
        },
        async loadOne ({ commit, getters }, { id, reFetch = false }) {
            if (!reFetch && getters.conversationById(id)) {
                return Promise.resolve();
            }

            try {
                const { data } = await fetchConversation(id);
                commit('setConversation', {
                    id: data.conversationId,
                    conversation: data,
                });

                return data;
            } catch (error) {
                throw error;
            }
        },
        removeLastExchange ({ commit }, { conversationId }) {
            commit('removeLastExchange', { conversationId });
        },
        async sendConversation (
            { commit, state, dispatch, rootState },
            { id, type, revision, onStreamRead = () => {}, onStreamEnd = () => {} },
        ) {
            dispatch('startLoading', id);
            const conversation = state.conversations[id];

            if (!conversation) {
                dispatch('stopLoading', id);
                throw new Error('Conversation not found');
            }

            const postRequest = getAskPendoPostRequest(type);
            if (!postRequest) {
                dispatch('stopLoading', id);
                throw new Error('Invalid conversation type');
            }

            // Cancel any existing stream for this conversation
            const existingController = state.abortControllers[conversation.conversationId];
            if (existingController) {
                existingController.abort();
                commit('clearAbortController', { conversationId: conversation.conversationId });
            }

            // Create new AbortController for this request
            const abortController = new AbortController();
            commit('setAbortController', { conversationId: conversation.conversationId, controller: abortController });

            try {
                const data = await postRequest(
                    conversation,
                    state.employeeConfig,
                    { revision, signal: abortController.signal },
                    rootState?.auth?.user?.jobRole || '',
                    state.customInstructions || '',
                );

                if (data instanceof ReadableStream) {
                    let stringBuffer = '';

                    // Record when this stream started
                    const streamStartTime = Date.now();
                    const streamId = `${conversation.conversationId}_${streamStartTime}`;

                    // Mark this as the active stream for this conversation
                    // This immediately invalidates ALL previous streams for this conversation
                    commit('setActiveStreamId', { conversationId: conversation.conversationId, streamId });

                    const streamingProcessor = new StreamingProcessor({
                        onData: (chunk) => {
                            const currentActiveStreamId = state.activeStreamIds[conversation.conversationId];
                            if (currentActiveStreamId !== streamId) {
                                return;
                            }
                            switch (chunk.type) {
                                case 'assistant':
                                    if (chunk.tool_calls.length > 0) {
                                        dispatch('addMessage', {
                                            conversationId: conversation.conversationId,
                                            type: 'assistant',
                                            content: '',
                                            data: chunk,
                                            streamId,
                                        });
                                    }
                                    stringBuffer += chunk.content;
                                    break;
                                case 'tool':
                                    dispatch('addMessage', {
                                        conversationId: conversation.conversationId,
                                        type: 'tool',
                                        content: chunk.content,
                                        data: chunk,
                                        streamId,
                                    });
                                    break;
                                case 'workflow_state':
                                    commit('setWorkflowState', {
                                        conversationId: conversation.conversationId,
                                        workflowState: chunk.workflow,
                                    });
                                    break;
                                case 'plan_state':
                                    commit('setPlanState', {
                                        conversationId: conversation.conversationId,
                                        planState: chunk.plan,
                                    });
                                    break;
                                case 'todo_update':
                                    commit('updateTodo', {
                                        conversationId: conversation.conversationId,
                                        updatedTodo: chunk.todo,
                                    });
                                    break;
                            }
                            onStreamRead({ ...chunk, streamId });
                        },
                        onEnd: (finalState) => {
                            const activeStreamId = state.activeStreamIds[conversation.conversationId];
                            const isActiveStream = activeStreamId === streamId;

                            // Handle case where finalState is null (e.g., when request is aborted)
                            if (!finalState || !stringBuffer || !isActiveStream) {
                                onStreamEnd();

                                return;
                            }

                            commit('setConversation', {
                                id: conversation.conversationId,
                                conversation: { conversationId: conversation.conversationId, ...finalState },
                            });

                            commit('clearActiveStreamId', { conversationId: conversation.conversationId });
                            onStreamEnd();
                        },
                        onError: (error) => {
                            throw error;
                        },
                    });

                    await streamingProcessor.process(data);

                    return;
                }

                if (data.body.error) {
                    dispatch('stopLoading', id);
                    throw new Error(data.body.error);
                }

                const conversationResponse = {
                    id,
                    ...data.body,
                    messages: data.body.messages.map((msg) => ({
                        ...msg,
                        timestamp: moment().toISOString(),
                    })),
                };
                commit('setConversation', { id, conversation: conversationResponse });
            } catch (error) {
                // Handle AbortError gracefully - don't show error to user when they click stop
                if (error.name === 'AbortError' || error.message.includes('aborted')) {
                    return; // Silent return, no error shown to user
                }
                throw new Error(
                    `An error occurred while generating the response. Try opening a new conversation.\n\nError: ${error.message}`,
                );
            } finally {
                dispatch('stopLoading', id);
                commit('clearAbortController', { conversationId: conversation.conversationId });
            }
        },
        setArtifactConfig ({ commit }, { artifactId, config }) {
            commit('setArtifactConfig', { artifactId, config });
        },
        setArtifactMenuItems ({ commit }, { artifactId, items }) {
            commit('setArtifactMenuItems', { artifactId, items });
        },
        setEmployeeConfigProperty ({ commit, state }, { key, value }) {
            const employeeConfig = {
                ...state.employeeConfig,
                [key]: value,
            };
            commit('setEmployeeConfig', { employeeConfig });
        },
        updateCustomInstructions ({ commit }, customInstructions) {
            commit('setCustomInstructions', { customInstructions: customInstructions || '' });
        },
        startLoading ({ commit }, conversationId) {
            commit('setResponsePending', { id: conversationId, isPending: true });
        },
        stopLoading ({ commit }, conversationId) {
            commit('setResponsePending', { id: conversationId, isPending: false });
        },
        stopResponse ({ dispatch, commit, state }, { conversationId }) {
            // Get the active stream ID before clearing it so we can target the right incomplete messages
            const activeStreamId = state.activeStreamIds[conversationId];

            // Abort the actual HTTP request using AbortController
            const controller = state.abortControllers[conversationId];
            if (controller) {
                controller.abort();
                commit('clearAbortController', { conversationId });
            }

            commit('clearActiveStreamId', { conversationId });
            // Clean up incomplete assistant messages to prevent chat history validation errors
            // Pass the streamId so we only clean up messages from the stream that was just stopped
            // Only run cleanup if we have a valid streamId to target
            if (activeStreamId) {
                commit('removeIncompleteMessages', { conversationId, streamId: activeStreamId });
            }
            dispatch('stopLoading', conversationId);
        },
        updateConfigDrawerState ({ commit }, { isOpen }) {
            commit('setConfigDrawerState', { isOpen });
        },
    },
    getters: {
        awaitingFirstResponse: (state) => (id) => {
            const convo = state.conversations[id];
            const hasMessages = convo && convo.messages;
            const isFirstMessage = hasMessages && convo.messages.length === 1;

            return isFirstMessage && convo.messages[0].type === 'user' && convo.messages[0].metadata?.heading_type !== 'ask_question';
        },
        conversationById: (state) => (id) => state.conversations[id] || null,
        getArtifactConfig: (state) => (artifactId) => state.artifactConfig[artifactId] || null,
        getArtifactMenuItems: (state) => (artifactId) => state.artifactMenuItems[artifactId] || [],
        getEmployeeConfig: (state) => state.employeeConfig,
        getCustomInstructions: (state) => state.customInstructions,
        isConfigDrawerOpen: (state) => state.configDrawerOpen,
        isResponsePending: (state) => (id) => state.pendingResponses[id] || false,
        userConversations: (state) => state.userConversations || [],
    },
};

export default askPendo;
